// Adaptation of a plan in progress: pure and idempotent function, evaluated after each workout.
// The rules (PlanContent.adapt) adjust the intensity, replace a workout that is too hard when tired,
// and skip optional workouts when falling behind. Nothing is dated by the clock: everything comes from `input`.
import { estimateLoad, type Workout } from '@gruvetto/core';
import type { PlanAdjust, PlanContent } from '@gruvetto/contracts';
import type { KindContext } from './kind.js';
import { planWorkout } from './kinds/plan.js';

const DAY = 86400000, WEEK = 7 * DAY, LOG_MAX = 30;
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const round2 = (x: number) => Math.round(x * 100) / 100;

export interface PlanProgress { start: number; done: Record<string, string>; adjust?: PlanAdjust }
export interface AdaptActivity { id: string; date: number; meta: { planKey?: string | null; compliance?: number | null; completed?: boolean }; summary: { TSS: number } }
export interface AdaptInput { activities: AdaptActivity[]; tsb: number; now: number; findWorkout: KindContext['findWorkout'] }
export interface AdaptResult { adjust: PlanAdjust; done: Record<string, string>; notes: string[] }

export const emptyAdjust = (): PlanAdjust => ({ intensity: 1, swaps: {}, evaluated: 0, log: [] });

/** Workout of a slot ("week-session"), replacements included. */
export function planSlot(content: PlanContent, key: string, adjust: PlanAdjust | undefined, ctx: Pick<KindContext, 'findWorkout'>): Workout | null {
  const [w, i] = key.split('-').map(Number), s = content.weeks[w]?.sessions[i];
  if (!s) return null;
  return planWorkout(content, adjust?.swaps[key] ?? s.workout, ctx);
}

/** Slots in plan order. */
const slots = (c: PlanContent) => c.weeks.flatMap((wk, w) => wk.sessions.map((s, i) => ({ key: `${w}-${i}`, week: w, session: s })));

export function adaptPlan(content: PlanContent, plan: PlanProgress, input: AdaptInput): AdaptResult {
  const a0 = plan.adjust ?? emptyAdjust(), adjust: PlanAdjust = { ...a0, swaps: { ...a0.swaps }, log: [...a0.log] };
  const done = { ...plan.done }, notes: string[] = [];
  const log = (text: string) => { notes.push(text); adjust.log = [...adjust.log, { at: input.now, text }].slice(-LOG_MAX); };
  const ctx = { findWorkout: input.findWorkout };
  const mine = input.activities.filter(a => a.meta.planKey && a.date >= plan.start).sort((a, b) => a.date - b.date);

  // compliance: each workout of the plan is evaluated only once
  for (const a of mine.filter(x => x.date > adjust.evaluated)) {
    const comp = a.meta.compliance;
    if (comp == null) continue;
    for (const r of content.adapt) {
      if (r.when === 'failed' && comp < r.complianceBelow) {
        const before = adjust.intensity;
        adjust.intensity = round2(clamp(before - r.easier, 0.7, 1.2));
        if (adjust.intensity !== before) log(`Workout held at ${comp}%: the next ones are lightened (${Math.round(adjust.intensity * 100)}%)`);
      } else if (r.when === 'crushed' && comp > r.complianceAbove) {
        const before = adjust.intensity;
        adjust.intensity = round2(clamp(before + r.harder, 0.7, 1.2));
        if (adjust.intensity !== before) log(`Workout held at ${comp}%: the next ones are a bit harder (${Math.round(adjust.intensity * 100)}%)`);
      }
    }
  }
  if (mine.length) adjust.evaluated = Math.max(adjust.evaluated, mine[mine.length - 1].date);

  // behind: more than N days without a plan workout
  const last = mine.length ? mine[mine.length - 1].date : plan.start;
  for (const r of content.adapt) {
    if (r.when !== 'missed' || input.now - last <= r.days * DAY) continue;
    const idle = Math.floor((input.now - last) / DAY);
    if (r.then === 'notice') { notes.push(`No plan workout for ${idle} days: pick it back up at your own pace`); continue; }
    const week = Math.floor((input.now - plan.start) / WEEK);
    const late = slots(content).filter(s => s.session.optional && s.week < week && !done[s.key]);
    for (const s of late) done[s.key] = 'skipped';
    if (late.length) log(`${idle} days without a workout: ${late.length} optional workout${late.length > 1 ? 's' : ''} skipped to catch up with the plan`);
  }

  // fatigue: the next intense workout becomes a recovery
  const next = slots(content).find(s => !done[s.key]);
  for (const r of content.adapt) {
    if (r.when !== 'fatigue' || !next || input.tsb >= r.tsbBelow || adjust.swaps[next.key]) continue;
    const wo = planSlot(content, next.key, adjust, ctx), easy = planWorkout(content, r.workout, ctx);
    if (!wo || !easy || estimateLoad(wo, 250).IF <= 0.85) continue;
    adjust.swaps = { ...adjust.swaps, [next.key]: r.workout };
    log(`Freshness ${Math.round(input.tsb)}: "${wo.name}" is replaced by "${easy.name}"`);
  }
  return { adjust, done, notes };
}
